import { useEffect, useState } from 'react';
import { Hotkeys, HotkeysActions, HotkeysBindings } from '@/services/user/userSettingsTypes';
import { twMerge } from 'tailwind-merge';

type HotkeyProps = {
  action: HotkeysActions;
  assignHotkey: (title: string, newHotkey: HotkeysBindings) => void;
  existingHotkeys: Hotkeys;
} & HotkeysBindings;

export const hotkeysNames: { [K in HotkeysActions]: string } = {
  call: 'Check/Call',
  fold: 'Fold',
  check: 'Check',
  bringIn: 'Bring In',
  confirm: 'Confirm',
  complete: 'Complete',
  betOrRaise: 'Bet/Raise',
} as const;

const isMacOS = () => {
  return /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
};

const displayKey = (keyCode: string) => {
  if (keyCode.includes('Key')) return keyCode.replace('Key', '');
  if (keyCode.includes('Digit')) return keyCode.replace('Digit', '');
  if (keyCode.includes('ArrowLeft')) return '←';
  if (keyCode.includes('ArrowRight')) return '→';
  if (keyCode.includes('ArrowUp')) return '↑';
  if (keyCode.includes('ArrowDown')) return '↓';

  return keyCode;
};

const Hotkey = ({ action, assignHotkey, ctrl, meta, keys, existingHotkeys }: HotkeyProps) => {
  const [isAssigningKey, setIsAssigningKey] = useState(false);
  const [isHotkeyAlreadyAssigned, setIsHotkeyAlreadyAssigned] = useState(false);

  const checkIfKeyIsAlreadyAssigned = (key: string, userHotkeys: Hotkeys) => {
    for (const binding of Object.values(userHotkeys)) {
      if (binding.keys.includes(key)) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (isAssigningKey) {
      const handleKeyDown = (e: KeyboardEvent) => {
        setIsAssigningKey(false);

        if (!checkIfKeyIsAlreadyAssigned(e.code, existingHotkeys)) {
          assignHotkey(action, { keys: [e.code], ctrl, meta });
        } else {
          setIsHotkeyAlreadyAssigned(true);
          setTimeout(() => {
            setIsHotkeyAlreadyAssigned(false);
          }, 700);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isAssigningKey, action, ctrl, meta, assignHotkey]);

  return (
    <div className="mb-4 flex h-auto w-44 select-none flex-col gap-3">
      <div className="w-full justify-center text-center text-sm font-semibold text-white">{hotkeysNames[action]}</div>

      <div className="flex items-center justify-center gap-2">
        {(ctrl || meta) && (
          <div className="w-15 rounded-md border border-ph-gray-jumbo p-2 text-center text-sm font-bold text-ph-gray-jumbo hover:cursor-not-allowed">
            {isMacOS() ? '⌘' : 'Ctrl'}
          </div>
        )}

        <div
          className={twMerge(
            'flex h-10 min-w-23 items-center justify-center rounded-md border border-ph-gray-jumbo p-2 text-center text-sm font-bold text-white hover:cursor-pointer hover:border-ph-secondary-portage hover:text-ph-secondary-portage',
            isAssigningKey && 'animate-pulse border-ph-secondary-portage text-[0.7em] text-ph-secondary-portage',
            isHotkeyAlreadyAssigned && 'border-ph-red text-[0.53em] text-ph-red',
          )}
          onClick={() => setIsAssigningKey((prev) => !prev)}
        >
          {!isAssigningKey && !isHotkeyAlreadyAssigned && keys[0].length > 0 && displayKey(keys[0])}
          {/* TODO for now we only use one key */}
          {isAssigningKey && !isHotkeyAlreadyAssigned && 'Press a key!'}
          {isHotkeyAlreadyAssigned && 'Already assigned'}
        </div>
      </div>
    </div>
  );
};

export default Hotkey;
